import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog, Box, Typography, Button, IconButton, TextField, InputAdornment, alpha, Zoom,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LockPersonRoundedIcon from '@mui/icons-material/LockPersonRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import { PDVThemeColors } from './PDVThemes';

interface DialogAutorizacaoGerenteProps {
  open: boolean;
  onClose: () => void;
  titulo: string;
  mensagem: string;
  colors: PDVThemeColors;
  corDestaque?: string;
  // Chama a ação de verdade (cancelar/devolução) já com usuário+senha — o
  // backend confere credencial E permissão numa chamada só (ver
  // AutorizarUsuarioPdv.ts/EstornarVendaPdv.ts no backend); não existe um
  // passo de "pré-autorizar" separado.
  onConfirmar: (login: string, senha: string) => Promise<unknown | Error>;
  onAutorizado: (resultado: unknown) => void;
}

// Modal de "autorização de gerente" — reutilizável pra qualquer ação do PDV
// que precise de usuário+senha de alguém com permissão, sem trocar a sessão
// logada (ver DialogResumoVendasPDV.tsx: cancelar/devolver venda). Quem
// digita NÃO precisa ser o operador logado.
export const DialogAutorizacaoGerente: React.FC<DialogAutorizacaoGerenteProps> = ({
  open, onClose, titulo, mensagem, colors, corDestaque, onConfirmar, onAutorizado,
}) => {
  const [login, setLogin] = useState('');
  const [senha, setSenha] = useState('');
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const loginRef = useRef<HTMLInputElement>(null);

  const cor = corDestaque || colors.primarySolid;

  useEffect(() => {
    if (open) {
      setLogin('');
      setSenha('');
      setMostrarSenha(false);
      setErro('');
      setEnviando(false);
      setTimeout(() => loginRef.current?.focus(), 100);
    }
  }, [open]);

  const confirmar = async () => {
    if (!login.trim() || !senha) {
      setErro('Informe usuário e senha.');
      return;
    }
    setEnviando(true);
    setErro('');
    const resultado = await onConfirmar(login.trim(), senha);
    setEnviando(false);
    if (resultado instanceof Error) {
      setErro(resultado.message);
      return;
    }
    onAutorizado(resultado);
  };

  return (
    <Dialog
      open={open}
      onClose={enviando ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      TransitionComponent={Zoom}
      TransitionProps={{ timeout: 200 }}
      sx={{ '& .MuiDialog-paper': { borderRadius: '16px', overflow: 'hidden' } }}
    >
      <Box sx={{
        px: 2.5, py: 1.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderBottom: `0.5px solid ${alpha('#000', 0.08)}`,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Box sx={{
            width: 34, height: 34, borderRadius: '8px', background: colors.primaryGradient, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <LockPersonRoundedIcon sx={{ fontSize: 18, color: colors.textOnPrimary }} />
          </Box>
          <Typography variant="subtitle1" fontWeight={600} noWrap>{titulo}</Typography>
        </Box>
        <IconButton size="small" onClick={onClose} disabled={enviando} sx={{ color: 'text.secondary' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ p: 2.5 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {mensagem}
        </Typography>

        <TextField
          inputRef={loginRef}
          label="Usuário"
          value={login}
          onChange={(e) => setLogin(e.target.value)}
          fullWidth
          size="small"
          disabled={enviando}
          sx={{ mb: 2 }}
          onKeyDown={(e) => { if (e.key === 'Enter') confirmar(); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PersonOutlineRoundedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label="Senha"
          type={mostrarSenha ? 'text' : 'password'}
          value={senha}
          onChange={(e) => setSenha(e.target.value)}
          fullWidth
          size="small"
          disabled={enviando}
          onKeyDown={(e) => { if (e.key === 'Enter') confirmar(); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setMostrarSenha((v) => !v)} edge="end">
                  {mostrarSenha ? <VisibilityOffRoundedIcon sx={{ fontSize: 18 }} /> : <VisibilityRoundedIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {erro && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1.5 }}>
            {erro}
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 1, mt: 2.5 }}>
          <Button fullWidth variant="outlined" disabled={enviando} onClick={onClose} sx={{ borderRadius: '10px' }}>
            Cancelar
          </Button>
          <Button
            fullWidth
            variant="contained"
            disabled={enviando}
            onClick={confirmar}
            sx={{
              borderRadius: '10px',
              background: cor,
              '&:hover': { background: alpha(cor, 0.85) },
            }}
          >
            {enviando ? 'Verificando...' : 'Autorizar'}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};
